import React, { useState, useEffect } from 'react';
import { Volume2, CheckCircle, XCircle, Star } from 'lucide-react';
import { useApp } from '../../lib/AppContext';
import { getRandomWords } from '../../lib/vocabulary';
import { speechService } from '../../services/speechService';
import { VocabularyWord } from '../../types';

const WORDS_PER_GAME = 10;
const POINTS_PER_WORD = 12;

interface MissingLetterPuzzle {
  word: VocabularyWord;
  missingIndex: number;
  missingLetter: string;
  displayWord: string;
  options: string[];
}

export function MissingLetterGame() {
  const { gameState, addScore, markWordLearned, showCelebration, endGame } = useApp();
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [puzzle, setPuzzle] = useState<MissingLetterPuzzle | null>(null);
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const currentSession = gameState.currentSession;

  // Initialize game
  useEffect(() => {
    if (currentSession) {
      const gameWords = getRandomWords(
        WORDS_PER_GAME,
        currentSession.level,
        currentSession.language
      );
      // Filter words that are at least 3 letters long
      const validWords = gameWords.filter((w) => w.word.length >= 3);
      setWords(validWords);
      setCurrentWordIndex(0);
      setScore(0);
      setCorrectCount(0);
    }
  }, [currentSession]);

  // Create puzzle when word changes
  useEffect(() => {
    if (words.length > 0 && currentWordIndex < words.length) {
      const currentWord = words[currentWordIndex];
      createPuzzle(currentWord);

      // Note: Auto-play removed - browsers block speech synthesis unless triggered by user interaction
      // Users must click the "Play Word" button to hear the word
    }
  }, [currentWordIndex, words]);

  const createPuzzle = (word: VocabularyWord) => {
    // Choose a random letter to hide (not first or last for easier difficulty)
    const wordLength = word.word.length;
    let missingIndex: number;

    if (currentSession && currentSession.level <= 2) {
      // For easy levels, avoid first and last letter
      missingIndex =
        Math.floor(Math.random() * (wordLength - 2)) + 1;
    } else {
      // For harder levels, any letter can be missing
      missingIndex = Math.floor(Math.random() * wordLength);
    }

    const missingLetter = word.word[missingIndex].toLowerCase();

    // Create display word with blank
    const displayWord =
      word.word.substring(0, missingIndex) +
      '_' +
      word.word.substring(missingIndex + 1);

    // Generate letter options (correct letter + 3 wrong letters)
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const wrongLetters: string[] = [];

    while (wrongLetters.length < 3) {
      const randomLetter = alphabet[Math.floor(Math.random() * alphabet.length)];
      if (
        randomLetter !== missingLetter &&
        !wrongLetters.includes(randomLetter)
      ) {
        wrongLetters.push(randomLetter);
      }
    }

    const options = [missingLetter, ...wrongLetters].sort(() => Math.random() - 0.5);

    setPuzzle({
      word,
      missingIndex,
      missingLetter,
      displayWord,
      options,
    });

    setSelectedLetter(null);
    setIsCorrect(null);
  };

  const playWord = () => {
    if (!puzzle || !currentSession) return;

    setIsSpeaking(true);
    speechService.speak(puzzle.word.word, currentSession.language, {
      rate: 0.8,
      onEnd: () => setIsSpeaking(false),
      onStart: () => setIsSpeaking(true),
    });
  };

  const handleLetterSelect = (letter: string) => {
    if (selectedLetter || !puzzle) return;

    setSelectedLetter(letter);
    const correct = letter === puzzle.missingLetter;
    setIsCorrect(correct);

    if (correct) {
      const points = POINTS_PER_WORD;
      setScore(score + points);
      setCorrectCount(correctCount + 1);
      addScore(points);
      markWordLearned(puzzle.word.id);
    }

    // Move to next word after delay
    setTimeout(() => {
      if (currentWordIndex + 1 >= words.length) {
        // Game complete
        handleGameComplete();
      } else {
        setCurrentWordIndex(currentWordIndex + 1);
      }
    }, 1500);
  };

  const handleGameComplete = () => {
    showCelebration();

    setTimeout(() => {
      endGame();
    }, 3000);
  };

  if (!currentSession || words.length === 0 || !puzzle) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading game...</div>
      </div>
    );
  }

  const progress = ((currentWordIndex + 1) / words.length) * 100;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold text-primary flex items-center gap-2">
              ⭐ Missing Letter
            </h2>
            <p className="text-gray-600 mt-1">Fill in the blank!</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <Star className="text-yellow-500 fill-yellow-500" />
              <span className="text-3xl font-bold text-yellow-600">{score}</span>
            </div>
            <p className="text-sm text-gray-500">Score</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-gradient-to-r from-pink-400 to-orange-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-sm text-gray-600 mt-2 text-center">
          Question {currentWordIndex + 1} of {words.length}
        </div>
      </div>

      {/* Main Game Area */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-6">
        {/* Sound Button */}
        <div className="text-center mb-8">
          <button
            onClick={playWord}
            disabled={isSpeaking}
            className={`btn-primary text-xl px-12 py-6 flex items-center gap-3 mx-auto ${
              isSpeaking ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            <Volume2 className={isSpeaking ? 'animate-pulse' : ''} size={32} />
            {isSpeaking ? 'Playing...' : 'Play Word'}
          </button>
        </div>

        {/* Word Display */}
        <div className="text-center mb-8">
          <div className="text-6xl font-bold text-gray-800 tracking-wider mb-4">
            {puzzle.displayWord.split('').map((char, idx) => (
              <span
                key={idx}
                className={`inline-block mx-1 ${
                  char === '_'
                    ? 'border-b-4 border-blue-500 w-12 h-16'
                    : ''
                }`}
              >
                {char !== '_' ? char : '\u00A0'}
              </span>
            ))}
          </div>

          {puzzle.word.category && (
            <div className="text-sm text-gray-500 mt-2">
              Hint: This is a <strong>{puzzle.word.category}</strong> word
            </div>
          )}
        </div>

        {/* Letter Options */}
        <div className="grid grid-cols-4 gap-4 max-w-md mx-auto mb-6">
          {puzzle.options.map((letter) => {
            const isSelected = selectedLetter === letter;
            const isCurrentCorrect = letter === puzzle.missingLetter;
            const showCorrect = selectedLetter && isCurrentCorrect;
            const showWrong = isSelected && !isCorrect;

            return (
              <button
                key={letter}
                onClick={() => handleLetterSelect(letter)}
                disabled={selectedLetter !== null}
                className={`
                  p-6 rounded-2xl text-4xl font-bold transition-all duration-300 uppercase
                  ${selectedLetter === null ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed'}
                  ${
                    showCorrect
                      ? 'bg-green-500 text-white shadow-lg scale-110'
                      : showWrong
                      ? 'bg-red-500 text-white shadow-lg'
                      : 'bg-white text-gray-800 shadow-md hover:shadow-xl border-2 border-gray-200'
                  }
                `}
              >
                {letter}
              </button>
            );
          })}
        </div>

        {/* Feedback */}
        {isCorrect !== null && (
          <div
            className={`animate-fadeIn text-center p-6 rounded-2xl ${
              isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            <div className="flex items-center justify-center gap-2 text-xl font-bold">
              {isCorrect ? (
                <>
                  <CheckCircle size={28} />
                  Excellent! +{POINTS_PER_WORD} points
                </>
              ) : (
                <>
                  <XCircle size={28} />
                  The correct letter was: {puzzle.missingLetter.toUpperCase()}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="bg-white rounded-3xl shadow-lg p-6">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-3xl font-bold text-green-600">{correctCount}</div>
            <div className="text-sm text-gray-600">Correct</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-red-600">
              {currentWordIndex + 1 - correctCount}
            </div>
            <div className="text-sm text-gray-600">Wrong</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-600">
              {Math.round((correctCount / (currentWordIndex + 1)) * 100)}%
            </div>
            <div className="text-sm text-gray-600">Accuracy</div>
          </div>
        </div>
      </div>
    </div>
  );
}
